import React, { useCallback } from 'react'
import EditWindowModal from '../../Designer/EditWindowModal.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import { openModal, closeModal } from 'actions/modalActions'
import { useDispatch, useSelector } from 'react-redux'
import { getContextName } from 'Helper/routeHelpers'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import _ from 'lodash'

const WindowSubmenu = ({ windows, inWorkflow }) => {
  const windowList = []
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const screenType = _.capitalize(getCurrentScreenType())
  const isDesignMode = useSelector(state => state.interactableState.uiDesignMode)
  const interactableWindows = useSelector(state => state.interactableState.windows) || {}
  const currentContext = getContextName(pathname)
  const showConfigure = isDesignMode && (currentContext === 'adminDisplay' || currentContext === 'patientDisplay' || currentContext === 'nurseDashboard')

  const addWindowModal = useCallback(() => {
    const closeSelf = () => dispatch(closeModal('edit_window_modal'))
    dispatch(openModal(
      'edit_window_modal',
      {
        title: 'Configure ' + screenType + ' Window',
        content: (
          <EditWindowModal
            closeSelf={closeSelf}
          />
        ),
        closeable: true
      })
    )
  }, [isDesignMode])

  if (!showConfigure  && (windows == null || windows.length === 0)) {
    return null
  } else {
    // https://stackoverflow.com/questions/8900732/sort-objects-in-an-array-alphabetically-on-one-property-of-the-array
    const windowList = windows.sort((a, b) => {
      const textA = a.name ? a.name.toUpperCase() : ''
      const textB = b.name ? b.name.toUpperCase() : ''
      return textA < textB ? -1 : textA > textB ? 1 : 0
    })
    return (
      <li className="dropdown-submenu">
        <a
          className="dropdown-toggle"
          id="windowsDropDown"
          role="button"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="true"
          aria-labelledby="windows menu"
        >
          <FontAwesomeIcon icon={['far', 'window-maximize']} /> Windows
        </a>
        <ul
          className="dropdown-menu dropdown-menu-right"
          aria-labelledby="windowsDropDown"
        >
          {windowList.map((window, idx) => {
            return (
              <WindowLink
                key={idx}
                window={window}
                inWorkflow={inWorkflow}
                interactableWindows={interactableWindows}
              />
            )
          })}
          {showConfigure
            ? <li key={'add window'}>
              <a
                role="button"
                aria-label={`add window link`}
                onClick={() => {
                  addWindowModal()
                }}
              >
                &lt;Add Window&gt;
              </a>
            </li>
            : null
          }
        </ul>
      </li>
    )
  }
}

WindowSubmenu.propTypes = {
  // An array of window objects for the window menu
  windows: PropTypes.arrayOf(
    PropTypes.shape({
      // Width to display window as percentage of screen
      width: PropTypes.string,
      // Height to display window as percentage of screen
      height: PropTypes.string,
      // X position of the upper left window corner as percentage of the screen
      x: PropTypes.string,
      // Y position of the upper left window corner as a percentage of the screen
      y: PropTypes.string,
      // User visible name of the window
      name: PropTypes.string,
      // the ref attached to the window component within the WindowContainer component
      ref: PropTypes.string.isRequired,
      // Should the window be visible in the window menu
      selectable: PropTypes.bool,
      inWorkflow: PropTypes.bool
    })
  )
}

export default WindowSubmenu

export const WindowLink = ({ window, inWorkflow, interactableWindows }) => {
  const dispatch = useDispatch()
  const clickHandler = e => {
    e.preventDefault()
    let windowConfig = window
    if (
      (window.modalCapable || window.modalConfig) &&
      inWorkflow
    ) {
      // if we are in a workflow, open the modal as a window
      windowConfig = { ...windowConfig, asModal: true }
    }
    dispatch({ type: 'UPDATE_WIDGET', window: windowConfig })
  }

  let disabled
  // Should be disabled if its already open, or, if its not modal capable (when we are in workflow)
  if (
    interactableWindows[window.ref] != null ||
    (!window.modalCapable &&
      !window.modalConfig &&
      inWorkflow)
  ) {
    disabled = 'disabled'
  }
  return (
    <li className={disabled}>
      <a
        id={window.ref}
        role="button"
        aria-label={`${window.name} window link`}
        onClick={disabled === 'disabled' ? () => null : clickHandler}
      >
        {window.name}
      </a>
    </li>
  )
}
